import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroupDirective,
  NgForm,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import * as emailAddresses from 'email-addresses';
import { Collaboration } from '@pncl/common-models';

// Case-insensitive so scheme, host and path accept capitals (e.g. HTTPS://Example.com/Survey?ID=ABC).
// Do not add the `u` flag: with it, `i` would also match look-alike characters such as the Kelvin
// sign and the long s against ASCII letters. Not global, so it is safe to share across .test() calls.
const URL_PATTERN = new RegExp(
  '^(https?:\\/\\/)?' + // validate protocol
    // validate domain name; label = alphanumeric runs joined by hyphens (no nested quantifiers,
    // so a failing match cannot backtrack exponentially)
    '(([a-z\\d]+(-+[a-z\\d]+)*\\.)+[a-z]{2,}|' +
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
    '(\\#.*)?$',
  'i',
);

/** Validates URLs against URL_PATTERN. Reports the standard `pattern` error. */
const urlPatternValidator: ValidatorFn = Validators.pattern(URL_PATTERN);

// REF: https://www.rfc-editor.org/errata_search.php?rfc=3696
// even thought recommendtion in rfc is 256 but angulars email validator Validation.email sets
// maxium to 254
export const EMAIL_MAX_LENGTH_THRESHOLD = 254;
export const PASSWORD_INPUT_LENGTH_THRESHOLD = 10;
export const PASSWORD_MAX_LENGTH_THRESHOLD = 64;
export const countDigits = (value: string): number => value.replace(/\D/g, '').length;

function noWhitespaceValidator(control: AbstractControl): ValidationErrors {
  const isWhitespace = (control.value || '').trim().length === 0;
  const isValid = !isWhitespace;
  return isValid ? {} : { whitespace: true };
}

function uniqueValuesValidator(control: AbstractControl): ValidationErrors | null {
  const formArray = control as FormArray;
  const formValues = formArray.controls.map((controller) => controller.get('value')?.value);

  const valueOccurrences = formValues.reduce((occurrences, value) => {
    occurrences[value] = (occurrences[value] || 0) + 1;
    return occurrences;
  }, {} as Record<string, number>);

  let hasDuplicates = false;

  formArray.controls.forEach((ctrl, index) => {
    hasDuplicates = hasDuplicates || valueOccurrences[ctrl.get('value')?.value] > 1;
    ctrl.setErrors(
      valueOccurrences[ctrl.get('value')?.value] > 1 ? { duplicateValues: true } : null,
    );
  });

  return hasDuplicates ? { duplicateValues: true } : null;
}

function atLeastOneValueValidator(control: AbstractControl): ValidationErrors | null {
  const formArray = control as FormArray;
  return formArray.length > 0 ? null : { noValues: true };
}

/*
 * Firebase only accepts emails that have a top domain, which is not required by
 * the regular email validator.
 * Regex generated using information from https://www.regular-expressions.info/email.html
 */
function firebaseEmailValidator(control: AbstractControl): ValidationErrors | null {
  const testEmail = control.value;
  return getEmailValidationErrors(testEmail);
}

function getEmailValidationErrors(email: string): ValidationErrors | null {
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,125}[a-zA-Z]{2,63}$/;
  try {
    if (email.match(emailRegex)) {
      return null;
    } else {
      return { email: true };
    }
  } catch (err) {
    return { email: true };
  }
}

/**
 * Parses and validates an email address, checking all necessary criteria:
 * 1) Ensures the email does not exceed a specific length threshold.
 * 2) Uses the `email-addresses` library for RFC-compliant parsing.
 * 3) Performs a post-parse validation for Firebase's domain requirements.
 * Returns the parsed email address if all checks pass, or null if any validation fails.
 */
function parseAndValidateEmail(email: string): string | null {
  try {
    const parsed = emailAddresses.parseOneAddress({
      input: email,
      atInDisplayName: true,
    }) as emailAddresses.ParsedMailbox;

    return parsed && isValidFirebaseEmail(parsed.address) ? parsed.address : null;
  } catch {
    return null;
  }
}

/**
 * Utility function to quickly check if an email is valid
 * for Firebase by the above rules.
 */
function isValidFirebaseEmail(email: string): boolean {
  return getEmailValidationErrors(email) === null;
}

class DuplicateValuesErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, _form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.parent && control.invalid && control.parent.dirty);
  }
}

/**
 * A flexible error state matcher that can be configured to show specific error types instantly,
 * while using the default behavior (show when touched or form submitted) for all other errors.
 *
 * @example
 * // Show maxlength errors instantly
 * const matcher = new InstantErrorDisplayMatcher(['maxlength']);
 *
 * @example
 * // Show ALL validation errors instantly without waiting for touch or submit
 * const matcher = new InstantErrorDisplayMatcher([]);
 */
class InstantErrorDisplayMatcher implements ErrorStateMatcher {
  /**
   * @param instantErrorTypes Array of error types to show instantly without waiting for touch or submit.
   * If empty array is provided, ALL validation errors will show instantly.
   */
  constructor(private readonly instantErrorTypes: string[]) {}

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    // No control or no errors, so no error state
    if (!control || !control.invalid) {
      return false;
    }

    // If instantErrorTypes is empty, show ALL validation errors immediately
    if (!this.instantErrorTypes || this.instantErrorTypes.length === 0) {
      return true;
    }

    // Check if any of the instant error types are present
    for (const errorType of this.instantErrorTypes) {
      if (control.errors?.[errorType]) {
        return true;
      }
    }

    // For all other errors, use the default behavior
    return !!(control.touched || form?.submitted);
  }
}

/**
 * Validates password fields according to application requirements:
 * - Minimum 10 characters
 * - Maximum 64 characters (NIST recommended)
 * - Must contain both letters and numbers
 */
const PASSWORD_VALIDATORS: ValidatorFn[] = [
  Validators.minLength(PASSWORD_INPUT_LENGTH_THRESHOLD),
  Validators.maxLength(PASSWORD_MAX_LENGTH_THRESHOLD),
  Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d).+$/),
];

/**
 * Validates name fields according to application requirements:
 * - Required field
 * - Max length validation
 * - No whitespace validation
 * @deprecated Use nameValidators instead, because it's more flexible
 */
const NAME_VALIDATORS: ValidatorFn[] = [
  Validators.required,
  Validators.maxLength(Collaboration.Constants.MAX_ENTITY_TITLE_LENGTH),
  noWhitespaceValidator,
];

/**
 * Validates email fields according to application requirements:
 * - Required field
 * - Standard email format validation
 * - Max length validation (255 characters)
 * - Firebase-specific email format validation
 */
const EMAIL_VALIDATORS: ValidatorFn[] = [
  Validators.required,
  Validators.email,
  Validators.maxLength(EMAIL_MAX_LENGTH_THRESHOLD),
  firebaseEmailValidator,
];

/**
 * Validates name fields according to application requirements:
 * - Required field
 * - Max length validation
 * - No whitespace validation
 * @param maxLength - Maximum length for the name field. Uses the default value from constant MAX_ENTITY_TITLE_LENGTH if not provided.
 * @returns An array of validators to apply to name fields
 */
function nameValidators(
  maxLength: number = Collaboration.Constants.MAX_ENTITY_TITLE_LENGTH,
): ValidatorFn[] {
  return [Validators.required, Validators.maxLength(maxLength), noWhitespaceValidator];
}

/**
 * Validates CSS color values according to CSS specification.
 * Supports both background and background-color validation modes.
 *
 * @param supportsGradients - Whether to validate against 'background' property (gradients) or 'background-color' (solid colors only)
 * @returns A validator function that validates CSS color/background values
 *
 * @example
 * // For solid colors only (background-color compatible)
 * const colorValidator = cssColorValidator(false);
 *
 * @example
 * // For gradients and complex backgrounds (background compatible)
 * const backgroundValidator = cssColorValidator(true);
 */
function cssColorValidator(supportsGradients: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Allow empty values
    }
    const targetProperty = supportsGradients ? 'background' : 'background-color';
    const isValid = CSS.supports(targetProperty, control.value.trim());
    return isValid ? null : { invalidCssColor: { value: control.value } };
  };
}

export {
  noWhitespaceValidator,
  uniqueValuesValidator,
  atLeastOneValueValidator,
  firebaseEmailValidator,
  getEmailValidationErrors,
  isValidFirebaseEmail,
  DuplicateValuesErrorStateMatcher,
  InstantErrorDisplayMatcher,
  URL_PATTERN,
  urlPatternValidator,
  parseAndValidateEmail,
  PASSWORD_VALIDATORS,
  NAME_VALIDATORS,
  EMAIL_VALIDATORS,
  nameValidators,
  cssColorValidator,
};
